import { useEffect, useState, Fragment } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { GLDrillDialog, ReconciliationFooter } from "@/components/FinancialStatementShared";
import { ChevronRight, ChevronDown, Landmark } from "lucide-react";

const SECTION_LABELS = {
  shareholders_funds: "1. Shareholders' Funds",
  non_current_liabilities: "2. Non-current Liabilities",
  current_liabilities: "3. Current Liabilities",
  non_current_assets: "1. Non-current Assets",
  current_assets: "2. Current Assets",
};

const Row = ({ row, depth = 0, onDrill, expanded, onToggle, rowKey }) => (
  <Fragment>
    <tr className={`border-b hover:bg-slate-50 ${row.mapped === false ? "bg-amber-50" : ""}`} data-testid={`bs-row-${rowKey}`}>
      <td className="p-2" style={{ paddingLeft: `${12 + depth * 18}px` }}>
        {row.ledgers?.length > 0 ? (
          <button className="flex items-center gap-1 text-left hover:text-orange-700" onClick={onToggle} data-testid={`bs-expand-${rowKey}`}>
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {row.schedule_head}
          </button>
        ) : row.schedule_head}
        {row.info && <span className="text-[10px] text-slate-400 ml-2">({row.info})</span>}
      </td>
      <td className="p-2 text-right tabular">{formatINR(row.current)}</td>
      <td className="p-2 text-right tabular text-slate-500">{row.previous ? formatINR(row.previous) : "—"}</td>
    </tr>
    {expanded && (row.ledgers || []).map((l, i) => (
      <tr key={i} className="border-b bg-slate-50/60 text-slate-600 cursor-pointer hover:bg-orange-50" onClick={() => onDrill(l)} data-testid={`bs-ledger-${rowKey}-${i}`}>
        <td className="p-1.5 text-xs" style={{ paddingLeft: `${40 + depth * 18}px` }}>
          {l.account_name} <span className="text-slate-400">· GL drill-down</span>
        </td>
        <td className="p-1.5 text-right tabular text-xs">{formatINR(l.balance)}</td>
        <td className="p-1.5 text-right text-xs text-slate-300">·</td>
      </tr>
    ))}
  </Fragment>
);

export default function BalanceSheetPage() {
  const [data, setData] = useState(null);
  const [asOn, setAsOn] = useState("");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    api.get("/branches").then(r => setBranches(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (asOn) qs.set("as_on", asOn);
    if (branchId) qs.set("branch_id", branchId);
    api.get(`/financial-statements/balance-sheet?${qs}`).then(r => setData(r.data)).catch(() => {});
  }, [asOn, branchId]);

  if (!data) return <div className="text-slate-500 p-6" data-testid="bs-loading">Loading Balance Sheet…</div>;

  const t = data.totals;
  const toggle = k => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const renderSection = (label, rows, keyPrefix) => (
    <Fragment key={keyPrefix}>
      <tr className="bg-slate-100">
        <td className="p-2 font-bold text-[11px] uppercase tracking-wider text-slate-600" colSpan={3}>{label}</td>
      </tr>
      {rows.length === 0 && (
        <tr className="border-b"><td className="p-2 pl-6 text-slate-400 text-xs" colSpan={3}>— Nil —</td></tr>
      )}
      {rows.map((r, i) => (
        <Row key={i} row={r} rowKey={`${keyPrefix}-${i}`} onDrill={setDrill}
             expanded={!!expanded[`${keyPrefix}-${i}`]} onToggle={() => toggle(`${keyPrefix}-${i}`)} />
      ))}
    </Fragment>
  );

  return (
    <div data-testid="balance-sheet-page">
      <PageHeader
        eyebrow="Statutory Reporting"
        title="Balance Sheet — Schedule III"
        subtitle={`Division I vertical format · FY ${data.fy_code} · as on ${data.as_on} · read directly from the verified General Ledger`}
        actions={
          <div className="flex items-center gap-2">
            <select value={branchId} onChange={e => setBranchId(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs bg-white" data-testid="bs-branch-filter">
              <option value="">Consolidated (All Branches)</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name || b.branch_code || b.id}</option>)}
            </select>
            <label className="text-xs text-slate-400">As on</label>
            <input type="date" value={asOn} onChange={e => setAsOn(e.target.value)} className="border border-slate-300 px-3 py-2 text-xs" data-testid="bs-ason-filter" />
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Assets</div>
          <div className="font-display text-2xl font-black tabular mt-2" data-testid="bs-total-assets">{formatINR(t.assets)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Equity & Liabilities</div>
          <div className="font-display text-2xl font-black tabular mt-2" data-testid="bs-total-eql">{formatINR(t.equity_and_liabilities)}</div>
        </Card>
        <Card className={`p-5 ${Math.abs(t.difference) < 0.01 ? "border-emerald-300" : "border-amber-300"}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2"><Landmark size={11} /> Difference</div>
          <div className={`font-display text-xl font-black tabular mt-2 ${Math.abs(t.difference) < 0.01 ? "text-emerald-700" : "text-amber-700"}`} data-testid="bs-difference">
            {formatINR(t.difference)}
          </div>
          {t.difference_explanation && <div className="text-[10px] text-amber-700 mt-1 leading-tight">{t.difference_explanation}</div>}
        </Card>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b bg-white">
              <th className="p-2 text-[11px] uppercase tracking-wider">Particulars</th>
              <th className="p-2 text-right text-[11px] uppercase tracking-wider w-44">Current Year<br /><span className="normal-case font-normal">FY {data.fy_code}</span></th>
              <th className="p-2 text-right text-[11px] uppercase tracking-wider w-44">Previous Year<br /><span className="normal-case font-normal">FY {data.previous_fy_code}</span></th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-slate-800 text-white"><td className="p-2 font-bold" colSpan={3}>I. EQUITY AND LIABILITIES</td></tr>
            {renderSection(SECTION_LABELS.shareholders_funds, data.equity_and_liabilities.shareholders_funds, "sf")}
            {renderSection(SECTION_LABELS.non_current_liabilities, data.equity_and_liabilities.non_current_liabilities, "ncl")}
            {renderSection(SECTION_LABELS.current_liabilities, data.equity_and_liabilities.current_liabilities, "cl")}
            <tr className="bg-slate-200 font-bold">
              <td className="p-2">TOTAL — EQUITY AND LIABILITIES</td>
              <td className="p-2 text-right tabular">{formatINR(t.equity_and_liabilities)}</td>
              <td className="p-2 text-right tabular text-slate-500">{formatINR(t.previous.equity_and_liabilities)}</td>
            </tr>
            <tr className="bg-slate-800 text-white"><td className="p-2 font-bold" colSpan={3}>II. ASSETS</td></tr>
            {renderSection(SECTION_LABELS.non_current_assets, data.assets.non_current_assets, "nca")}
            {renderSection(SECTION_LABELS.current_assets, data.assets.current_assets, "ca")}
            <tr className="bg-slate-200 font-bold">
              <td className="p-2">TOTAL — ASSETS</td>
              <td className="p-2 text-right tabular">{formatINR(t.assets)}</td>
              <td className="p-2 text-right tabular text-slate-500">{formatINR(t.previous.assets)}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      <ReconciliationFooter recon={data.reconciliation} testid="bs-reconciliation" />
      <GLDrillDialog open={!!drill} onClose={() => setDrill(null)} account={drill} />
    </div>
  );
}
